import type jsPDF from 'jspdf';

/** Matches SelfAssessmentReport / Conor Harris marketing typography. */
export const PDF_FONT_BODY = 'Roboto';
export const PDF_FONT_HEADING = 'Poppins';

const FONT_FILES = {
  robotoRegular: {
    vfsName: 'Roboto-Regular.ttf',
    family: PDF_FONT_BODY,
    style: 'normal' as const,
    url: 'https://cdn.jsdelivr.net/gh/googlefonts/roboto@main/src/hinted/Roboto-Regular.ttf',
  },
  robotoBold: {
    vfsName: 'Roboto-Bold.ttf',
    family: PDF_FONT_BODY,
    style: 'bold' as const,
    url: 'https://cdn.jsdelivr.net/gh/googlefonts/roboto@main/src/hinted/Roboto-Bold.ttf',
  },
  poppinsBold: {
    vfsName: 'Poppins-Bold.ttf',
    family: PDF_FONT_HEADING,
    style: 'bold' as const,
    url: 'https://cdn.jsdelivr.net/gh/google/fonts@main/ofl/poppins/Poppins-Bold.ttf',
  },
};

const fontBase64Cache = new Map<string, string>();
let fontBytesPromise: Promise<void> | null = null;
const registeredDocs = new WeakSet<object>();

async function arrayBufferToBase64(buffer: ArrayBuffer): Promise<string> {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

async function loadFontBytes(): Promise<void> {
  if (fontBytesPromise) {
    await fontBytesPromise;
    return;
  }

  fontBytesPromise = (async () => {
    await Promise.all(
      Object.values(FONT_FILES).map(async (font) => {
        if (fontBase64Cache.has(font.vfsName)) return;
        const response = await fetch(font.url);
        if (!response.ok) {
          throw new Error(`Failed to load PDF font ${font.vfsName}: ${response.status}`);
        }
        const base64 = await arrayBufferToBase64(await response.arrayBuffer());
        fontBase64Cache.set(font.vfsName, base64);
      }),
    );
  })();

  await fontBytesPromise;
}

/** Register Roboto + Poppins on a jsPDF document (idempotent per doc instance). */
export async function applyPdfBrandFonts(doc: jsPDF): Promise<void> {
  await loadFontBytes();

  if (registeredDocs.has(doc)) {
    return;
  }

  for (const font of Object.values(FONT_FILES)) {
    const base64 = fontBase64Cache.get(font.vfsName);
    if (!base64) continue;
    doc.addFileToVFS(font.vfsName, base64);
    doc.addFont(font.vfsName, font.family, font.style);
  }

  registeredDocs.add(doc);
}

export function setPdfBodyFont(doc: jsPDF, style: 'normal' | 'bold' = 'normal'): void {
  doc.setFont(PDF_FONT_BODY, style);
}

export function setPdfHeadingFont(doc: jsPDF): void {
  doc.setFont(PDF_FONT_HEADING, 'bold');
}

export const PDF_TABLE_FONT_STYLES = {
  font: PDF_FONT_BODY,
  fontStyle: 'normal' as const,
};

export const PDF_TABLE_HEAD_FONT_STYLES = {
  font: PDF_FONT_BODY,
  fontStyle: 'bold' as const,
};
